import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { loadSim } from "@/lib/api";
import { emailRe, errorFields, errorText, submitLead } from "@/lib/leads";

const COPY = {
  recevoir: {
    title: "Recevez votre simulation",
    subtitle:
      "Indiquez vos coordonnées pour recevoir le détail de votre simulation directement par email.",
    button: "Recevoir par email",
    legal:
      "En validant, vous recevrez votre simulation par email et acceptez d'être éventuellement recontacté à propos de votre projet immobilier.",
    endpoint: "/simulations/send-result",
    successTitle: "Simulation envoyée",
  },
  expert: {
    title: "Parlez-en à un expert",
    subtitle:
      "Laissez vos coordonnées, un conseiller vous rappelle pour affiner votre stratégie. Gratuit et sans engagement.",
    button: "Être recontacté",
    legal:
      "En validant, vous acceptez d'être recontacté au sujet de votre projet immobilier.",
    endpoint: "/leads",
    successTitle: "Demande envoyée",
  },
};

export default function LeadForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get("mode") === "expert" ? "expert" : "recevoir";
  const copy = COPY[mode];

  const [form, setForm] = useState({ prenom: "", nom: "", email: "", telephone: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: false }));
  };

  const validate = () => {
    const e = {};
    if (!form.prenom.trim()) e.prenom = true;
    if (!form.nom.trim()) e.nom = true;
    if (!emailRe.test(form.email.trim())) e.email = true;
    const digits = form.telephone.replace(/\D/g, "");
    if ((mode === "expert" || digits) && digits.length < 8) e.telephone = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.error("Merci de vérifier vos informations.");
      return;
    }
    setLoading(true);
    try {
      await submitLead(copy.endpoint, form, loadSim() || {});
      toast.dismiss();
      setSuccess(true);
    } catch (e) {
      const fields = errorFields(e);
      if (fields) setErrors((prev) => ({ ...prev, ...Object.fromEntries(fields.map((f) => [f, true])) }));
      toast.error(errorText(fields));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    const text =
      mode === "recevoir"
        ? `Merci ${form.prenom} ! Votre simulation vient d'être envoyée à ${form.email}. Pensez à vérifier vos courriers indésirables.`
        : `Merci ${form.prenom} ! Un conseiller vous recontactera très prochainement au sujet de votre projet.`;
    return (
      <div className="flex flex-col min-h-screen px-6 pt-8 pb-12 justify-center" data-testid="lead-success">
        <div className="animate-fade-up">
          <div className="h-14 w-14 rounded-full bg-[#2E7D32]/10 flex items-center justify-center mb-6">
            <CheckCircle2 strokeWidth={1.5} className="h-8 w-8 text-[#2E7D32]" />
          </div>
          <h1 className="font-serif text-3xl font-semibold text-[#231C18]">
            {copy.successTitle}
          </h1>
          <p className="mt-4 text-base text-[#8C7A70] leading-relaxed">{text}</p>
          <button
            data-testid="lead-success-home"
            onClick={() => navigate("/")}
            className="mt-8 h-[56px] w-full bg-[#C84C2C] text-white rounded-full font-semibold shadow-[0_8px_20px_-6px_rgba(200,76,44,0.55)] transition-transform active:scale-[0.98] hover:bg-[#B23E20]"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const fields = [
    { key: "prenom", label: "Prénom", type: "text", placeholder: "Marie" },
    { key: "nom", label: "Nom", type: "text", placeholder: "Durand" },
    { key: "email", label: "Email", type: "email", placeholder: "marie@email.com" },
    { key: "telephone", label: "Téléphone", optional: mode !== "expert", type: "tel", placeholder: "06 12 34 56 78" },
  ];

  return (
    <div className="flex flex-col min-h-screen px-6 pt-6 pb-40" data-testid="lead-form-screen">
      <button
        onClick={() => navigate(-1)}
        data-testid="lead-back-btn"
        className="h-9 w-9 -ml-2 mb-4 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]"
      >
        <ArrowLeft strokeWidth={1.5} className="h-5 w-5" />
      </button>

      <div className="animate-fade-up">
        <h1 className="font-serif text-3xl font-semibold text-[#231C18]">{copy.title}</h1>
        <p className="mt-2 text-sm text-[#8C7A70] leading-relaxed">{copy.subtitle}</p>

        <div className="mt-7 space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-semibold text-[#231C18] mb-2">
                {f.label}
                {f.optional && <span className="ml-1 font-normal text-[#8C7A70]">(facultatif)</span>}
              </label>
              <input
                data-testid={`lead-input-${f.key}`}
                type={f.type}
                value={form[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                placeholder={f.placeholder}
                className={`h-[56px] w-full bg-white shadow-[0_1px_2px_rgba(35,28,24,0.04)] border rounded-2xl px-4 text-[#231C18] text-base font-medium outline-none transition-all placeholder-[#8C7A70]/60 ${
                  errors[f.key]
                    ? "border-[#C62828] ring-1 ring-[#C62828]"
                    : "border-[#E6DDD4] focus:border-[#C84C2C] focus:ring-4 focus:ring-[#C84C2C]/15"
                }`}
              />
              {f.key === "email" && (
                <p className="mt-1.5 text-xs text-[#8C7A70] leading-snug" data-testid="lead-privacy-note">
                  Vos données sont traitées conformément à notre{" "}
                  <a
                    href="https://www.assertimmo.com/mentions-legales-assertimmo"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="lead-privacy-link"
                    className="text-[#C84C2C] font-semibold underline underline-offset-2"
                  >
                    politique de confidentialité
                  </a>
                  .
                </p>
              )}
            </div>
          ))}
        </div>

        <p className="mt-5 text-xs text-[#8C7A70] leading-relaxed">{copy.legal}</p>
        <a
          href="https://www.assertimmo.com/cgu-assertimmo"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="lead-cgu-link"
          className="mt-2 inline-block text-xs font-semibold text-[#C84C2C] underline underline-offset-2"
        >
          Politique de confidentialité
        </a>
      </div>

      <div className="fixed bottom-0 inset-x-0 mx-auto max-w-[480px] w-full px-6 py-6 bg-gradient-to-t from-[#FAF6F0] via-[#FAF6F0] to-transparent z-40 space-y-3">
        <button
          onClick={submit}
          disabled={loading}
          data-testid="lead-submit-btn"
          className="h-[56px] w-full bg-[#C84C2C] text-white rounded-full font-semibold shadow-[0_8px_20px_-6px_rgba(200,76,44,0.55)] transition-transform active:scale-[0.98] hover:bg-[#B23E20] disabled:opacity-60"
        >
          {loading ? "Envoi en cours…" : copy.button}
        </button>
        <button
          onClick={() => navigate("/")}
          data-testid="lead-cancel-btn"
          className="w-full text-sm text-[#8C7A70] hover:text-[#231C18] transition-colors"
        >
          Non merci, retour à l'accueil
        </button>
      </div>
    </div>
  );
}
